import React, { useEffect } from "react";
import { Title } from "../../../stories/Title/Title";
import { Box } from "@mantine/core";
import { Switch } from "../../../stories/Switch/Switch";
import { getShadeBrandByShade } from "../../../utils/item.utils";
import { Controller, useFormContext } from "react-hook-form";
import { Product, RemovableItem } from "../../../types/interfaces";
import { WorkflowType } from "../../../types/enums";
import { MaterialAndShadeComponent } from "./MaterialAndShadeComponent";

type MaterialAndShadeSelectionProps = {
  removableItemChoicesData: any;
  product: Product;
};

export const MaterialAndShadeSelection: React.FC<
  MaterialAndShadeSelectionProps
> = ({ removableItemChoicesData, product }) => {
  const {
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext();

  const removableItem = [...product?.removableItem];

  const toggleMultiItem = watch("toggleMultiItem");

  const hasMaterialAndShade = (item1: RemovableItem, item2: RemovableItem) => {
    return (
      item1.itemMaterial &&
      item2.itemMaterial &&
      item1.teethShade &&
      item2.teethShade &&
      item1.gingivaShade &&
      item2.gingivaShade
    );
  };

  const isSameMaterialAndShade = (
    item1: RemovableItem,
    item2: RemovableItem
  ) => {
    if (hasMaterialAndShade(item1, item2)) {
      return (
        item1.itemMaterial.id === item2.itemMaterial.id &&
        item1.teethShade.id === item2.teethShade.id &&
        item1.gingivaShade.id === item2.gingivaShade.id
      );
    }
    return false;
  };

  useEffect(() => {
    if (removableItem.length === 1) {
      setValue("toggleMultiItem", false);
    } else {
      if (
        !hasMaterialAndShade(removableItem[0], removableItem[1]) ||
        isSameMaterialAndShade(removableItem[0], removableItem[1])
      ) {
        setValue("toggleMultiItem", false);
      } else {
        setValue("toggleMultiItem", true);
      }
    }
  }, [product]);

  useEffect(() => {
    if (removableItem.length === 1) {
      if (removableItem[0]?.itemMaterial) {
        setValue("material0", product.removableItem[0].itemMaterial.id);
      } else {
        setValue("material0", null);
      }
      if (
        removableItem[0]?.teethShade &&
        removableItem[0]?.workflowType !== WorkflowType.WAX_RIM
      ) {
        const shadeBrand = getShadeBrandByShade(
          removableItemChoicesData?.getItemShadesWhere ?? [],
          removableItem[0].teethShade.id
        );
        setValue("brand0", shadeBrand);
        setValue("shade0", removableItem[0].teethShade.id);
      } else {
        setValue("brand0", null);
        setValue("shade0", null);
      }
      if (
        removableItem[0]?.gingivaShade &&
        removableItem[0]?.workflowType !== WorkflowType.WAX_RIM
      ) {
        setValue("gingivaShade0", removableItem[0].gingivaShade.id);
      } else {
        setValue("gingivaShade0", null);
      }
    } else {
      if (toggleMultiItem === false) {
        removableItem
          ?.sort((a, b) => (a.teeth?.[0] ?? 0) - (b.teeth?.[0] ?? 0))
          ?.forEach((item, index) => {
            if (removableItem[0]?.itemMaterial) {
              setValue(`material${index}`, removableItem[0].itemMaterial.id);
            } else {
              setValue(`material${index}`, null);
            }
            if (
              removableItem[0]?.teethShade &&
              removableItem[0]?.workflowType !== WorkflowType.WAX_RIM
            ) {
              const shadeBrand = getShadeBrandByShade(
                removableItemChoicesData?.getItemShadesWhere ?? [],
                removableItem[0].teethShade.id
              );
              setValue(`brand${index}`, shadeBrand);
              setValue(`shade${index}`, removableItem[0].teethShade.id);
            } else {
              setValue(`brand${index}`, null);
              setValue(`shade${index}`, null);
            }
            if (
              removableItem[0]?.gingivaShade &&
              removableItem[0]?.workflowType !== WorkflowType.WAX_RIM
            ) {
              setValue(
                `gingivaShade${index}`,
                removableItem[0].gingivaShade.id
              );
            } else {
              setValue(`gingivaShade${index}`, null);
            }
          });
      } else {
        removableItem
          ?.sort((a, b) => (a.teeth?.[0] ?? 0) - (b.teeth?.[0] ?? 0))
          ?.forEach((item, index) => {
            if (item?.itemMaterial) {
              setValue(`material${index}`, item.itemMaterial.id);
            } else {
              setValue(`material${index}`, null);
            }
            if (
              item?.teethShade &&
              item?.workflowType !== WorkflowType.WAX_RIM
            ) {
              const shadeBrand = getShadeBrandByShade(
                removableItemChoicesData?.getItemShadesWhere ?? [],
                item.teethShade.id
              );
              setValue(`brand${index}`, shadeBrand);
              setValue(`shade${index}`, item.teethShade.id);
            } else {
              setValue(`brand${index}`, null);
              setValue(`shade${index}`, null);
            }
            if (
              item?.gingivaShade &&
              item?.workflowType !== WorkflowType.WAX_RIM
            ) {
              setValue(`gingivaShade${index}`, item.gingivaShade.id);
            } else {
              setValue(`gingivaShade${index}`, null);
            }
          });
      }
    }
  }, [product, toggleMultiItem]);

  return (
    <Box pt="lg">
      <Title
        title={
          removableItem[0]?.workflowType === WorkflowType.WAX_RIM
            ? "Matériau - prothèse amovible partielle"
            : "Matériau et teinte - prothèse amovible partielle"
        }
        size={3}
        mb="md"
      />
      {removableItem.length > 1 && (
        <Controller
          control={control}
          name="toggleMultiItem"
          defaultValue={false}
          render={({ field }) => (
            <Switch
              label="Caractéristiques différentes pour chaque prothèse"
              checked={field.value ?? false}
              onChange={() => field.onChange(!field.value)}
            />
          )}
        />
      )}
      {toggleMultiItem === false ? (
        <MaterialAndShadeComponent
          control={control}
          errors={errors}
          removableItemChoicesData={removableItemChoicesData}
          watch={watch}
          setValue={setValue}
          number={0}
          key={0}
          toggleMultiItem={toggleMultiItem}
          workflowType={removableItem[0]?.workflowType}
          removableItemLength={product?.removableItem.length}
        />
      ) : (
        removableItem.map((item, index) => (
          <MaterialAndShadeComponent
            control={control}
            errors={errors}
            removableItemChoicesData={removableItemChoicesData}
            watch={watch}
            setValue={setValue}
            number={index}
            key={index}
            toggleMultiItem={toggleMultiItem}
            removableItem={item}
            workflowType={item?.workflowType}
          />
        ))
      )}
    </Box>
  );
};
